import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { PaperPlaneTilt, CheckCircle, WarningCircle, CircleNotch, Copy, Trash, ArrowClockwise, Key, ShieldWarning, QrCode, Gauge, Pulse, Terminal, X, SealCheck } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';

const DOProxyDeployer = () => {
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState({ regions: [], sizes: [], ubuntu_slug: '', token_valid: true });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [form, setForm] = useState({ account_id: '', region: '', size: 's-1vcpu-1gb', count: 1, backups: false, ipv6: false, monitoring: true });
  const [results, setResults] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [proxies, setProxies] = useState([]);
  const [qrProxy, setQrProxy] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const setRowBusy = (id, kind, v) => setBusy(b => ({ ...b, [`${id}:${kind}`]: v }));

  const fetchProxies = async () => {
    try { const r = await axios.get('/api/do/proxies', { withCredentials: true }); setProxies(r.data); }
    catch (e) {}
  };

  useEffect(() => {
    axios.get('/api/accounts', { withCredentials: true })
      .then(r => { const act = (r.data || []).filter(a => a.status === 'active'); setAccounts(act); if (act.length) setForm(f => ({ ...f, account_id: act[0].id })); })
      .catch(() => toast.error('Failed to load DO accounts'));
    fetchProxies();
  }, []);

  useEffect(() => {
    if (!form.account_id) return;
    setLoadingMeta(true);
    axios.get(`/api/do/proxy-meta/${form.account_id}`, { withCredentials: true })
      .then(r => {
        setMeta(r.data);
        const regs = r.data.regions || [];
        const sgp = regs.find(x => x.slug === 'sgp1');
        setForm(f => ({ ...f, region: (sgp ? sgp.slug : (regs[0]?.slug || '')), size: r.data.default_size || 's-1vcpu-1gb' }));
      })
      .catch(() => setMeta({ regions: [], sizes: [], ubuntu_slug: '', token_valid: false }))
      .finally(() => setLoadingMeta(false));
  }, [form.account_id]);

  const handleDeploy = async () => {
    if (!form.account_id) return toast.error('Select a DO account');
    if (!form.region) return toast.error('Select a region');
    setDeploying(true);
    try {
      const res = await axios.post('/api/do/proxies', {
        account_id: form.account_id, region: form.region, size: form.size,
        backups: form.backups, ipv6: form.ipv6, monitoring: form.monitoring, count: form.count,
      }, { withCredentials: true });
      setResults(res.data.map(d => ({ ...d, public_ip: '', links: {} })));
      res.data.forEach((dep, index) => {
        if (dep.status === 'failed') return;
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const pr = await axios.get(`/api/do/proxies/${dep.id}`, { withCredentials: true });
            setResults(prev => { const a = [...prev]; a[index] = { ...a[index], status: pr.data.status, stage: pr.data.stage, public_ip: pr.data.public_ip, secret: pr.data.secret, links: pr.data.links || {} }; return a; });
            if (pr.data.status !== 'active' && pr.data.status !== 'failed' && attempts < 40) setTimeout(poll, 6000);
            else fetchProxies();
          } catch (e) { if (attempts < 40) setTimeout(poll, 8000); }
        };
        setTimeout(poll, 4000);
      });
      toast.success('Droplet(s) provisioning. This takes 1-3 min, then ~1 min for the proxy to boot.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Deploy failed'); }
    setDeploying(false);
  };

  const del = async (id) => {
    if (!window.confirm('Destroy this proxy droplet? This deletes the DO droplet permanently.')) return;
    try { await axios.delete(`/api/do/proxies/${id}`, { withCredentials: true }); toast.success('Destroyed'); fetchProxies(); setResults(prev => prev.filter(r => r.id !== id)); }
    catch (e) { toast.error('Failed to destroy'); }
  };

  const copy = (v, label) => { navigator.clipboard.writeText(v); toast.success(`${label} copied`); };

  const runBenchmark = async (id) => {
    setRowBusy(id, 'bench', true);
    try {
      const r = await axios.post(`/api/do/proxies/${id}/benchmark`, {}, { withCredentials: true });
      setProxies(prev => prev.map(p => p.id === id ? { ...p, benchmark: r.data, benchmarked_at: new Date().toISOString() } : p));
      toast.success(`Benchmark: TLS ${r.data.tls_handshake_ms ?? '—'}ms`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Benchmark failed'); }
    setRowBusy(id, 'bench', false);
  };

  const runDiagnostics = async (proxy) => {
    setDiag({ proxy, data: null });
    setDiagLoading(true);
    try {
      const r = await axios.post(`/api/do/proxies/${proxy.id}/diagnostics`, {}, { withCredentials: true });
      setDiag({ proxy, data: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Diagnostics failed');
      setDiag(null);
    }
    setDiagLoading(false);
  };

  const perfColor = (ms) => ms == null ? 'text-slate-500' : ms < 120 ? 'text-[#39FF14]' : ms < 300 ? 'text-yellow-400' : 'text-red-400';

  const statusBadge = (row) => {
    const s = row.status;
    const stage = row.stage;
    if (s === 'active') return <span className="text-[#39FF14] text-xs font-bold flex items-center gap-1"><CheckCircle /> active</span>;
    if (s === 'failed') return <span className="text-red-500 text-xs font-bold flex items-center gap-1"><WarningCircle /> failed</span>;
    const label = { creating: 'Creating droplet…', bootstrapping: 'Bootstrapping…', 'health-checking': 'Health-checking…' }[stage] || (stage || s);
    return <span className="text-yellow-400 text-xs font-bold flex items-center gap-1"><CircleNotch className="animate-spin" /> {label}</span>;
  };

  const selectedSize = meta.sizes.find(s => s.slug === form.size);
  const unitPrice = selectedSize ? selectedSize.price_monthly : 6;
  const estimatedTotal = (unitPrice * (form.count || 1)).toFixed(2);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black text-[#00E5FF] flex items-center gap-3"><PaperPlaneTilt size={32} weight="fill" /> MTPROTO PROXY</h1>
        <p className="text-slate-400 font-mono">Deploy Telegram MTProto proxy droplets on DigitalOcean.</p>
      </div>

      <div className="bg-card border border-white/10 p-8 max-w-3xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">DigitalOcean Account</label>
            <select value={form.account_id} onChange={(e) => setForm({ ...form, account_id: e.target.value })} data-testid="proxy-account" className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#00E5FF] appearance-none rounded-none">
              <option value="">-- account --</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Region {loadingMeta && <CircleNotch size={12} className="inline animate-spin" />}</label>
            <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="proxy-region" className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#00E5FF] appearance-none rounded-none">
              <option value="">-- region --</option>
              {meta.regions.map(r => <option key={r.slug} value={r.slug}>{r.name} ({r.slug})</option>)}
            </select>
          </div>
        </div>

        {meta.token_valid === false && (
          <div className="border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
            <ShieldWarning size={22} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">This DO account's API token is invalid or lacks droplet permission. Add a valid <strong>Personal Access Token with Write scope</strong> for this account (DigitalOcean → API → Tokens). Droplet creation will fail until then.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Droplet Plan</label>
            <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} data-testid="proxy-size" className="w-full bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#00E5FF] appearance-none rounded-none font-mono text-sm">
              {meta.sizes.length === 0 && <option value="s-1vcpu-1gb">s-1vcpu-1gb (Basic 1GB)</option>}
              {meta.sizes.map(s => <option key={s.slug} value={s.slug}>{s.slug} · {s.memory}MB · {s.vcpus}vCPU · ${s.price_monthly}/mo</option>)}
            </select>
            {selectedSize && <p className="text-[10px] text-slate-500 mt-1">~${selectedSize.price_monthly}/mo billed by DigitalOcean</p>}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Number of Dedicated Proxy Servers</label>
            <input type="number" min="1" max="20" value={form.count} onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) || 1 })} data-testid="proxy-count" className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono text-xl focus:outline-none focus:border-[#00E5FF]" />
            <p className="text-[11px] text-slate-400 mt-2" data-testid="proxy-cost-estimate">Each proxy creates a separate DigitalOcean Droplet. Estimated total: <span className="text-[#00E5FF] font-bold">${estimatedTotal}/month</span>.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-6 pt-2">
          {[['monitoring', 'DO Monitoring'], ['backups', 'Backups (extra cost)'], ['ipv6', 'Enable IPv6']].map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 cursor-pointer" onClick={() => setForm({ ...form, [k]: !form[k] })} data-testid={`proxy-toggle-${k}`}>
              <div className={`w-5 h-5 border flex items-center justify-center ${form[k] ? 'bg-[#00E5FF] border-[#00E5FF]' : 'border-white/20'}`}>{form[k] && <CheckCircle size={16} weight="bold" className="text-black" />}</div>
              <span className="text-sm text-slate-300">{l}</span>
            </label>
          ))}
        </div>

        <div className="text-[11px] text-slate-500 font-mono border-t border-white/10 pt-3">
          Image: <span className="text-slate-300">{meta.ubuntu_slug || 'ubuntu-24-04-x64'}</span> · Port 443 · A unique secret is generated per proxy.
        </div>

        <button onClick={handleDeploy} disabled={deploying || meta.token_valid === false} data-testid="proxy-deploy-btn" className="w-full bg-[#00E5FF] text-black px-8 py-4 font-black uppercase tracking-wider hover:bg-[#00c4dd] transition-colors disabled:opacity-50">
          DEPLOY {form.count} {form.count > 1 ? 'PROXIES' : 'PROXY'}
        </button>
      </div>

      {results.length > 0 && (
        <div className="bg-card border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 bg-black/20"><h3 className="font-bold uppercase tracking-wider text-[#00E5FF]">Deployment Results</h3></div>
          <div className="divide-y divide-white/5">
            {results.map((d, i) => (
              <div key={i} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold">{d.name}</span>
                  {statusBadge(d)}
                </div>
                {d.status === 'failed' && <p className="text-red-500 text-xs">{d.error_msg}</p>}
                {d.status === 'active' && d.links?.tg_link && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="bg-white p-2 w-fit rounded">
                      <QRCodeSVG value={d.links.tg_link} size={104} />
                    </div>
                    <div className="md:col-span-2 space-y-2 text-xs font-mono">
                      <div className="flex items-center gap-2"><span className="text-slate-500">IP:</span> <span className="text-white">{d.public_ip}</span></div>
                      <div className="flex items-center gap-2"><Key size={14} className="text-slate-500" /><span className="text-slate-400 truncate">{d.secret}</span><button onClick={() => copy(d.secret, 'Secret')} className="text-slate-400 hover:text-white"><Copy size={14} /></button></div>
                      <div className="flex items-center gap-2 bg-[#0A0B0E] border border-white/10 p-2">
                        <span className="text-[#00E5FF] truncate flex-1">{d.links.tg_link}</span>
                        <button onClick={() => copy(d.links.tg_link, 'Telegram link')} className="text-slate-400 hover:text-white"><Copy size={16} /></button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10 bg-black/20 flex justify-between items-center">
          <h3 className="font-bold uppercase tracking-wider text-[#00E5FF]">Active Proxies</h3>
          <button onClick={fetchProxies} className="text-slate-400 hover:text-white p-1"><ArrowClockwise size={18} /></button>
        </div>
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-white/10 text-xs text-slate-400 uppercase">
            <th className="p-4">Name</th><th className="p-4">Region</th><th className="p-4">Status</th><th className="p-4">IP</th><th className="p-4">Performance</th><th className="p-4">Telegram Link</th><th className="p-4 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {proxies.map(p => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="p-4 font-mono font-bold">{p.name}</td>
                <td className="p-4 font-mono text-slate-400">{p.region}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    {statusBadge(p)}
                    {p.verified && <span title="FakeTLS handshake verified" className="text-[#00E5FF]"><SealCheck size={16} weight="fill" /></span>}
                  </div>
                </td>
                <td className="p-4 font-mono text-slate-400">{p.public_ip || '—'}</td>
                <td className="p-4">
                  {p.benchmark ? (
                    <div className="font-mono text-xs leading-tight" data-testid={`proxy-perf-${p.id}`}>
                      <div className={perfColor(p.benchmark.tls_handshake_ms)}>TLS {p.benchmark.tls_handshake_ms ?? '—'}ms</div>
                      <div className="text-slate-500">TCP {p.benchmark.tcp_connect_ms ?? '—'}ms</div>
                    </div>
                  ) : <span className="text-slate-600 text-xs">—</span>}
                </td>
                <td className="p-4">
                  {p.status === 'active' && p.links?.tg_link ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[#00E5FF] font-mono text-xs truncate max-w-[160px]">{p.links.tg_link}</span>
                      <button onClick={() => copy(p.links.tg_link, 'Link')} className="text-slate-400 hover:text-white" title="Copy link"><Copy size={14} /></button>
                      <button onClick={() => setQrProxy(p)} className="text-slate-400 hover:text-[#00E5FF]" title="Show QR"><QrCode size={16} /></button>
                    </div>
                  ) : '—'}
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    {p.public_ip && (
                      <>
                        <button onClick={() => runBenchmark(p.id)} disabled={busy[`${p.id}:bench`]} data-testid={`proxy-benchmark-${p.id}`} className="bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 hover:bg-[#00E5FF] hover:text-black transition-colors px-3 py-1 font-bold text-xs flex items-center gap-1 uppercase tracking-wider disabled:opacity-50" title="Re-measure latency">
                          {busy[`${p.id}:bench`] ? <CircleNotch size={14} className="animate-spin" /> : <Gauge size={14} />} Bench
                        </button>
                        <button onClick={() => runDiagnostics(p)} data-testid={`proxy-diagnostics-${p.id}`} className="bg-white/5 text-slate-200 border border-white/10 hover:bg-white/10 transition-colors px-3 py-1 font-bold text-xs flex items-center gap-1 uppercase tracking-wider" title="Run diagnostics">
                          <Terminal size={14} /> Diag
                        </button>
                      </>
                    )}
                    <button onClick={() => del(p.id)} data-testid={`proxy-destroy-${p.id}`} className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-colors px-3 py-1 font-bold text-xs flex items-center gap-1 uppercase tracking-wider"><Trash size={14} /> Destroy</button>
                  </div>
                </td>
              </tr>
            ))}
            {proxies.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-slate-500">No proxies deployed yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {qrProxy && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setQrProxy(null)}>
          <div className="bg-card border border-[#00E5FF]/40 p-8 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-[#00E5FF] mb-1">{qrProxy.name}</h3>
            <p className="text-xs text-slate-500 font-mono mb-4">Scan in Telegram to connect</p>
            <div className="bg-white p-4 inline-block rounded">
              <QRCodeSVG value={qrProxy.links.tg_link} size={220} />
            </div>
            <div className="mt-4 flex items-center gap-2 bg-[#0A0B0E] border border-white/10 p-2">
              <span className="text-[#00E5FF] font-mono text-xs truncate flex-1">{qrProxy.links.tg_link}</span>
              <button onClick={() => copy(qrProxy.links.tg_link, 'Link')} className="text-slate-400 hover:text-white"><Copy size={16} /></button>
            </div>
            <button onClick={() => setQrProxy(null)} className="mt-4 w-full bg-[#00E5FF] text-black font-bold py-2 hover:bg-[#00c4dd] transition-colors">CLOSE</button>
          </div>
        </div>
      )}

      {diag && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setDiag(null)} data-testid="proxy-diag-modal">
          <div className="bg-card border border-[#00E5FF]/40 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10 bg-black/30 flex items-center justify-between sticky top-0">
              <h3 className="font-bold text-[#00E5FF] flex items-center gap-2"><Pulse size={20} weight="fill" /> Diagnostics · {diag.proxy.name}</h3>
              <button onClick={() => setDiag(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">
              {diagLoading && <div className="flex items-center gap-2 text-slate-400 text-sm"><CircleNotch className="animate-spin" /> Probing droplet (external checks + SSH)…</div>}
              {diag.data && (
                <>
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">External Reachability</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#0A0B0E] border border-white/10 p-3 flex items-center gap-2">
                        {diag.data.external.tcp_443_open ? <CheckCircle className="text-[#39FF14]" /> : <WarningCircle className="text-red-500" />}
                        <span className="text-sm">TCP 443 open</span>
                      </div>
                      <div className="bg-[#0A0B0E] border border-white/10 p-3 flex items-center gap-2">
                        {diag.data.external.faketls_handshake_ok ? <CheckCircle className="text-[#39FF14]" /> : <WarningCircle className="text-red-500" />}
                        <span className="text-sm">FakeTLS handshake</span>
                      </div>
                    </div>
                  </div>

                  {diag.data.validation && (
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Config Integrity (runtime ↔ link ↔ DB)</h4>
                      <div className="bg-[#0A0B0E] border border-white/10 p-3 space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          {diag.data.validation.checks.db_equals_link ? <CheckCircle className="text-[#39FF14]" /> : <WarningCircle className="text-red-500" />}
                          <span>Database secret matches tg:// link</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {diag.data.validation.checks.runtime_equals_db === true && <CheckCircle className="text-[#39FF14]" />}
                          {diag.data.validation.checks.runtime_equals_db === false && <WarningCircle className="text-red-500" />}
                          {diag.data.validation.checks.runtime_equals_db == null && <WarningCircle className="text-slate-500" />}
                          <span className={diag.data.validation.checks.runtime_equals_db == null ? 'text-slate-500' : ''}>
                            Runtime mtg config matches database secret{diag.data.validation.checks.runtime_equals_db == null ? ' (no SSH — not checked)' : ''}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 pt-1 text-[10px] font-mono text-slate-500">
                          <div>db: <span className="text-slate-300">{diag.data.validation.fingerprints.db || '—'}</span></div>
                          <div>link: <span className="text-slate-300">{diag.data.validation.fingerprints.link || '—'}</span></div>
                          <div>runtime: <span className="text-slate-300">{diag.data.validation.fingerprints.runtime || '—'}</span></div>
                        </div>
                        {diag.data.validation.ssh_error && <p className="text-yellow-400/70 text-[11px]">{diag.data.validation.ssh_error}</p>}
                        <div className={`text-xs font-bold ${diag.data.validation.match ? 'text-[#39FF14]' : 'text-red-500'}`} data-testid="proxy-validation-result">
                          {diag.data.validation.match ? '✓ All checked values match' : '✗ MISMATCH DETECTED'}
                        </div>
                      </div>
                    </div>
                  )}

                  {diag.data.metrics && (
                    <div>
                      <h4 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">In-Droplet Health</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div className="bg-[#0A0B0E] border border-white/10 p-3">
                          <div className="text-[10px] uppercase text-slate-500">mtg service</div>
                          <div className={diag.data.metrics.mtg_up ? 'text-[#39FF14] font-bold' : 'text-red-500 font-bold'}>{diag.data.metrics.mtg_up ? 'running' : 'down'}</div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-white/10 p-3">
                          <div className="text-[10px] uppercase text-slate-500">Listening :443</div>
                          <div className={diag.data.metrics.listening_443 ? 'text-[#39FF14] font-bold' : 'text-red-500 font-bold'}>{diag.data.metrics.listening_443 ? 'yes' : 'no'}</div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-white/10 p-3">
                          <div className="text-[10px] uppercase text-slate-500">RTT → Cloudflare</div>
                          <div className={perfColor(diag.data.metrics.rtt_cloudflare_ms)}>{diag.data.metrics.rtt_cloudflare_ms ?? '—'} ms</div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-white/10 p-3">
                          <div className="text-[10px] uppercase text-slate-500">RTT → Google</div>
                          <div className={perfColor(diag.data.metrics.rtt_google_ms)}>{diag.data.metrics.rtt_google_ms ?? '—'} ms</div>
                        </div>
                        <div className="bg-[#0A0B0E] border border-white/10 p-3">
                          <div className="text-[10px] uppercase text-slate-500">RTT → Telegram</div>
                          <div className={perfColor(diag.data.metrics.rtt_telegram_ms)}>{diag.data.metrics.rtt_telegram_ms ?? '—'} ms</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {diag.data.ssh_error && <p className="text-yellow-400/80 text-xs bg-yellow-400/5 border border-yellow-400/20 p-3">{diag.data.ssh_error}</p>}

                  {diag.data.raw && (
                    <details className="group">
                      <summary className="cursor-pointer text-xs uppercase tracking-widest text-slate-500 font-bold hover:text-slate-300 select-none">Raw diagnostics ▾</summary>
                      <div className="mt-3 space-y-3">
                        {Object.entries(diag.data.raw).map(([k, v]) => (
                          <div key={k}>
                            <div className="text-[10px] uppercase text-slate-500 font-mono">{k}</div>
                            <pre className="bg-black/50 border border-white/10 p-2 text-[11px] text-slate-300 font-mono whitespace-pre-wrap break-all">{v || '(empty)'}</pre>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DOProxyDeployer;
